/**
 * NGワードのヒット箇所を本文中にマーキングするための、テキスト分割ユーティリティ。
 *
 * 【なぜ単純な replace ではダメか】
 * 従来の highlightText() は「選択中の1ワード」だけを対象にしていたため replace で
 * 済んでいたが、全ワードを同時にマークする場合は下記の理由で破綻する:
 *   1. 挿入したHTML(class名等)に後続のワードがマッチしてしまう
 *   2. 「Apple」と「Apple Watch」のように範囲が重複・内包するケースを表現できない
 *   3. 「ABC」と「BCD」が「ABCD」で交差するケースはHTMLの入れ子では表現不可能
 *
 * 【方針】
 * 先に全ワードのヒット範囲を収集し、すべての境界点(開始・終了)でテキストを分割する。
 * 各断片は「その位置を覆っているヒットの集合」を持つ。表示側は集合の最大レベルの
 * 色で1本の下線を引き、内訳は詳細ペインに出す(レベルの高いものを隠さないため、
 * 色は必ず最大レベル側を採用する)。
 */
import type { NgWordMatch } from '@/types/api'

/** 表示・フィルタ上のレベル区分。VB版 levelClass と同じ 9 / 7 / それ以下 の3段。 */
export type NgLevelBucket = 9 | 7 | 5

export function levelBucket(level: number): NgLevelBucket {
  if (level >= 9) return 9
  if (level >= 7) return 7
  return 5
}

/** 本文の一区間と、そこを覆っているNGワードヒット */
export interface NgTextSegment {
  text: string
  hits: NgWordMatch[]
  /** hits の最大レベル(hitsが空なら0)。下線の色・線種はこれで決まる */
  maxLevel: number
}

interface Range {
  start: number
  end: number
  hit: NgWordMatch
}

/**
 * ひらがな/カタカナ・大文字小文字を区別しない正規表現を組み立てる。
 * 既存の OfferDetailPanel.highlightText と同じ判定規則を踏襲している
 * (左のリストの件数と本文のマーク数がズレないようにするため)。
 */
function buildFlexibleRegex(word: string): RegExp | null {
  if (!word) return null
  const pattern = word
    .split('')
    .map((char) => {
      const code = char.charCodeAt(0)
      // ひらがな → 対応するカタカナも許容
      if (code >= 0x3041 && code <= 0x3096) {
        return `[${char}${String.fromCharCode(code + 0x0060)}]`
      }
      // カタカナ → 対応するひらがなも許容
      if (code >= 0x30a1 && code <= 0x30f6) {
        return `[${String.fromCharCode(code - 0x0060)}${char}]`
      }
      return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    })
    .join('')
  try {
    return new RegExp(pattern, 'gi')
  } catch {
    return null
  }
}

/** 1ワードのヒット範囲をすべて収集する */
function collectRanges(text: string, hit: NgWordMatch): Range[] {
  const regex = buildFlexibleRegex(hit.word)
  if (!regex) return []
  const ranges: Range[] = []
  let m: RegExpExecArray | null
  while ((m = regex.exec(text)) !== null) {
    if (m[0].length === 0) {
      regex.lastIndex++ // 空マッチによる無限ループ防止
      continue
    }
    ranges.push({ start: m.index, end: m.index + m[0].length, hit })
  }
  return ranges
}

/**
 * 本文を「マークなし区間」と「マークあり区間」に分割する。
 *
 * @param text      対象の本文(商品名・特徴・商品説明など)
 * @param ngWords   NGワード判定の結果一式
 * @param enabled   表示対象のレベル区分(チェックボックスで絞り込んだ結果)
 */
export function buildNgSegments(
  text: string | null | undefined,
  ngWords: NgWordMatch[] | null | undefined,
  enabled: Set<NgLevelBucket>,
  /**
   * このテキストが getNgWords のどのフィールドに対応するか
   * ("title" / "features" / "description" など)。
   * 指定すると、サーバーが返した matches の位置をそのまま使う。
   */
  field?: string,
): NgTextSegment[] {
  if (!text) return []
  if (!ngWords || ngWords.length === 0 || enabled.size === 0) {
    return [{ text, hits: [], maxLevel: 0 }]
  }

  const ranges: Range[] = []
  for (const w of ngWords) {
    if (!enabled.has(levelBucket(w.level))) continue

    // 【サーバーの照合結果を優先する】
    // サーバーは NormalizeWithMap(NFKC + 小書きカナ→大書き + 小文字化)で
    // 照合し、元テキスト上の正確な位置を matches として返す。
    // クライアント側の正規表現はひらがな/カタカナの相互変換しか見ておらず、
    // 例えばNGワード「うつ」が本文「うっかり」の「うっ」にマッチする、といった
    // 小書きカナの吸収ができない。位置計算を二重に持つと必ずズレるため、
    // matches があるときはそれをそのまま使う。
    const serverMatches = field
      ? (w.matches ?? []).filter((m) => m.field === field)
      : null

    if (serverMatches && serverMatches.length > 0) {
      for (const m of serverMatches) {
        const start = m.start
        const end = m.start + m.length
        // 想定外の範囲(テキスト更新中など)は無視して描画崩れを防ぐ
        if (start < 0 || end > text.length || start >= end) continue
        ranges.push({ start, end, hit: w })
      }
      continue
    }

    // matches が無い場合のフォールバック(古いレスポンス互換・フィールド未指定時)
    ranges.push(...collectRanges(text, w))
  }
  if (ranges.length === 0) return [{ text, hits: [], maxLevel: 0 }]

  // すべての境界点で切る(重複・内包・交差のいずれも、この方法なら一様に扱える)
  const boundaries = new Set<number>([0, text.length])
  for (const r of ranges) {
    boundaries.add(r.start)
    boundaries.add(r.end)
  }
  const points = [...boundaries].sort((a, b) => a - b)

  const segments: NgTextSegment[] = []
  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i]
    const to = points[i + 1]
    if (from === to) continue

    // この区間を覆っているヒット(レベル降順・同レベルは長いものを先に)
    const hits = ranges
      .filter((r) => r.start <= from && r.end >= to)
      .sort((a, b) => b.hit.level - a.hit.level || b.end - b.start - (a.end - a.start))
      .map((r) => r.hit)

    // 同一ワードが重複して入らないように(同じワードが複数回ヒットした場合など)
    const uniq: NgWordMatch[] = []
    const seen = new Set<string>()
    for (const h of hits) {
      if (seen.has(h.word)) continue
      seen.add(h.word)
      uniq.push(h)
    }

    segments.push({
      text: text.slice(from, to),
      hits: uniq,
      maxLevel: uniq.length ? Math.max(...uniq.map((h) => h.level)) : 0,
    })
  }

  // 隣接する区間を結合する。
  //  - マークなし同士: DOMノード数を減らすため
  //  - マークあり同士: 「Apple」と「Apple Watch」が重なると境界で断片が割れるが、
  //    見た目は1本の下線であるべきなので1つのマークにまとめ、ヒットは合併する
  //    (件数バッジも「Apple」側だけに付く、といった不自然さを避ける)
  const merged: NgTextSegment[] = []
  for (const seg of segments) {
    const prev = merged[merged.length - 1]
    if (prev && prev.hits.length === 0 && seg.hits.length === 0) {
      prev.text += seg.text
      continue
    }
    if (prev && prev.hits.length > 0 && seg.hits.length > 0) {
      prev.text += seg.text
      for (const h of seg.hits) {
        if (!prev.hits.some((p) => p.word === h.word)) prev.hits.push(h)
      }
      prev.hits.sort((a, b) => b.level - a.level)
      prev.maxLevel = Math.max(...prev.hits.map((h) => h.level))
      continue
    }
    merged.push(seg)
  }
  return merged
}
